/**
 * PWA Service - Comprehensive Progressive Web App functionality
 * Handles service worker registration, caching, offline support, and updates
 */

import { Workbox } from 'workbox-window';

export interface PWAUpdateInfo {
  isUpdateAvailable: boolean;
  skipWaiting: () => Promise<void>;
  registration?: ServiceWorkerRegistration;
}

export interface PWAInstallInfo {
  isInstallable: boolean;
  install: () => Promise<void>;
  isInstalled: boolean;
  platform: 'standalone' | 'browser' | 'unknown';
}

export interface PWAOfflineInfo {
  isOnline: boolean;
  connectionType?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

export interface PWAPerformanceInfo {
  cacheSize: number;
  cacheHits: number;
  networkRequests: number;
  offlineRequests: number;
  lastUpdated: string;
}

class PWAService {
  private workbox: Workbox | null = null;
  private updateCallbacks: Array<(info: PWAUpdateInfo) => void> = [];
  private offlineCallbacks: Array<(isOffline: boolean) => void> = [];
  private installPrompt: Event | null = null;
  private isInitialized = false;

  /**
   * Initialize PWA service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Initialize service worker
      await this.initializeServiceWorker();
      
      // Setup network monitoring
      this.setupNetworkMonitoring();
      
      // Setup install prompt handling
      this.setupInstallPrompt();
      
      // Setup performance monitoring
      this.setupPerformanceMonitoring();
      
      this.isInitialized = true;
      console.log('🚀 PWA Service initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize PWA service:', error);
      throw error;
    }
  }

  /**
   * Initialize service worker with Workbox
   */
  private async initializeServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️ Service Worker not supported');
      return;
    }

    // Skip service worker registration in development
    if (import.meta.env.DEV || import.meta.env.VITE_DISABLE_PWA === 'true') {
      console.log('🚫 Service Worker registration skipped in development mode');
      return;
    }

    try {
      this.workbox = new Workbox('/sw.js', {
        scope: '/',
        type: 'module'
      });

      // Listen for waiting service worker
      this.workbox.addEventListener('waiting', (event) => {
        const updateInfo: PWAUpdateInfo = {
          isUpdateAvailable: true,
          skipWaiting: async () => {
            if (event.sw && 'postMessage' in event.sw) {
              // Tell the waiting SW to skip waiting
              event.sw.postMessage({ type: 'SKIP_WAITING' });
              // Wait for the new SW to take control
              await new Promise<void>((resolve) => {
                const handleControllerChange = () => {
                  navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
                  resolve();
                };
                navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
              });
              // Reload to use the new service worker
              window.location.reload();
            }
          }
        };
        
        this.notifyUpdateCallbacks(updateInfo);
      });

      // Listen for controlling service worker
      this.workbox.addEventListener('controlling', () => {
        console.log('🔄 New service worker is controlling the page');
        window.location.reload();
      });

      // Register the service worker
      const registration = await this.workbox.register();
      console.log('✅ Service Worker registered:', registration);

    } catch (error) {
      console.error('❌ Service Worker registration failed:', error);
      throw error;
    }
  }

  /**
   * Setup network monitoring
   */
  private setupNetworkMonitoring(): void {
    const updateOnlineStatus = () => {
      const isOffline = !navigator.onLine;
      this.notifyOfflineCallbacks(isOffline);
      
      if (isOffline) {
        this.showOfflineNotification();
      }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    // Initial check
    updateOnlineStatus();
  }

  /**
   * Setup install prompt handling
   */
  private setupInstallPrompt(): void {
    window.addEventListener('beforeinstallprompt', (event) => {
      // Prevent the default install prompt
      event.preventDefault();
      
      // Store the install prompt for later use
      this.installPrompt = event;
      
      console.log('📱 App install prompt available');
    });

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      console.log('🎉 App installed successfully');
      this.installPrompt = null;
    });
  }

  /**
   * Setup performance monitoring
   */
  private setupPerformanceMonitoring(): void {
    // Monitor cache performance every 5 minutes
    setInterval(async () => {
      try {
        const performance = await this.getPerformanceInfo();
        console.log('📊 PWA Performance:', performance);
      } catch (error) {
        console.error('❌ Failed to get performance info:', error);
      }
    }, 5 * 60 * 1000);
  }

  /**
   * Register for update notifications
   */
  onUpdateAvailable(callback: (info: PWAUpdateInfo) => void): () => void {
    this.updateCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.updateCallbacks.indexOf(callback);
      if (index > -1) {
        this.updateCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Register for offline notifications
   */
  onOfflineChange(callback: (isOffline: boolean) => void): () => void {
    this.offlineCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.offlineCallbacks.indexOf(callback);
      if (index > -1) {
        this.offlineCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Get installation info
   */
  getInstallInfo(): PWAInstallInfo {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone ||
                        document.referrer.includes('android-app://');

    return {
      isInstallable: !!this.installPrompt,
      isInstalled: isStandalone,
      platform: isStandalone ? 'standalone' : 'browser',
      install: async () => {
        if (!this.installPrompt) {
          throw new Error('App install prompt not available');
        }

        const promptEvent = this.installPrompt as any;
        const result = await promptEvent.prompt();
        
        if (result.outcome === 'accepted') {
          console.log('✅ User accepted the install prompt');
          this.installPrompt = null;
        } else {
          console.log('❌ User dismissed the install prompt');
        }
      }
    };
  }

  /**
   * Get network information
   */
  getNetworkInfo(): PWAOfflineInfo {
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    return {
      isOnline: navigator.onLine,
      connectionType: connection?.type,
      effectiveType: connection?.effectiveType,
      downlink: connection?.downlink,
      rtt: connection?.rtt
    };
  }

  /**
   * Get performance information
   */
  async getPerformanceInfo(): Promise<PWAPerformanceInfo> {
    try {
      if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
        throw new Error('Service Worker not available');
      }

      // Get performance data from service worker
      const channel = new MessageChannel();
      const performancePromise = new Promise<any>((resolve, reject) => {
        channel.port1.onmessage = (event) => {
          if (event.data.error) {
            reject(new Error(event.data.error));
          } else {
            resolve(event.data);
          }
        };

        setTimeout(() => {
          reject(new Error('Performance data request timeout'));
        }, 5000);
      });

      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_PERFORMANCE_DATA' },
        [channel.port2]
      );

      const data = await performancePromise;

      // Calculate cache size
      const cacheSize = await this.calculateCacheSize();

      return {
        cacheSize,
        cacheHits: data.cacheHits || 0,
        networkRequests: data.networkRequests || 0,
        offlineRequests: data.offlineRequests || 0,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Failed to get performance info:', error);
      return {
        cacheSize: 0,
        cacheHits: 0,
        networkRequests: 0,
        offlineRequests: 0,
        lastUpdated: new Date().toISOString()
      };
    }
  }

  /**
   * Clear all caches
   */
  async clearCaches(): Promise<void> {
    try {
      if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
        throw new Error('Service Worker not available');
      }

      const channel = new MessageChannel();
      const clearPromise = new Promise<void>((resolve, reject) => {
        channel.port1.onmessage = (event) => {
          if (event.data.success) {
            resolve();
          } else {
            reject(new Error(event.data.error || 'Failed to clear caches'));
          }
        };

        setTimeout(() => {
          reject(new Error('Clear cache request timeout'));
        }, 10000);
      });

      navigator.serviceWorker.controller.postMessage(
        { type: 'CLEAR_CACHE' },
        [channel.port2]
      );

      await clearPromise;
      console.log('✅ All caches cleared successfully');
    } catch (error) {
      console.error('❌ Failed to clear caches:', error);
      throw error;
    }
  }

  /**
   * Force update the service worker
   */
  async forceUpdate(): Promise<void> {
    try {
      if (!this.workbox) {
        throw new Error('Workbox not initialized');
      }

      const registration = await this.workbox.register();
      if (registration) {
        await registration.update();
        console.log('🔄 Service Worker update check completed');
      }
    } catch (error) {
      console.error('❌ Failed to force update:', error);
      throw error;
    }
  }

  /**
   * Calculate total cache size
   */
  private async calculateCacheSize(): Promise<number> {
    try {
      if (!('caches' in window)) return 0;

      const cacheNames = await caches.keys();
      let totalSize = 0;

      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const requests = await cache.keys();
        
        for (const request of requests) {
          const response = await cache.match(request);
          if (response) {
            const blob = await response.blob();
            totalSize += blob.size;
          }
        }
      }

      return totalSize;
    } catch (error) {
      console.error('❌ Failed to calculate cache size:', error);
      return 0;
    }
  }

  /**
   * Show offline notification
   */
  private showOfflineNotification(): void {
    // This would integrate with your notification system
    console.log('📱 App is now offline. Some features may be limited.');
  }

  /**
   * Notify update callbacks
   */
  private notifyUpdateCallbacks(info: PWAUpdateInfo): void {
    this.updateCallbacks.forEach(callback => {
      try {
        callback(info);
      } catch (error) {
        console.error('❌ Update callback error:', error);
      }
    });
  }

  /**
   * Notify offline callbacks
   */
  private notifyOfflineCallbacks(isOffline: boolean): void {
    this.offlineCallbacks.forEach(callback => {
      try {
        callback(isOffline);
      } catch (error) {
        console.error('❌ Offline callback error:', error);
      }
    });
  }

  /**
   * Check if PWA features are supported
   */
  isSupported(): boolean {
    return !!(
      'serviceWorker' in navigator &&
      'caches' in window &&
      'fetch' in window
    );
  }

  /**
   * Get PWA capabilities
   */
  getCapabilities() {
    return {
      serviceWorker: 'serviceWorker' in navigator,
      cache: 'caches' in window,
      fetch: 'fetch' in window,
      notification: 'Notification' in window,
      geolocation: 'geolocation' in navigator,
      deviceMotion: 'DeviceMotionEvent' in window,
      deviceOrientation: 'DeviceOrientationEvent' in window,
      bluetooth: 'bluetooth' in navigator,
      webShare: 'share' in navigator,
      webgl: !!document.createElement('canvas').getContext('webgl'),
      webrtc: !!(window as any).RTCPeerConnection,
      mediaDevices: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    };
  }
}

// Export singleton instance
export const pwaService = new PWAService();
export default pwaService;